import type { ProjectDefinition } from "../../../types";
import { type CellValue, type FuncionProjectSpec, conditions, excelDate, seededRandom } from "../spec";

const SHEET = "AG Tickets";
const TABLE = "Tickets";
const RESUMEN = "ResumenAgentes";

const AREAS = ["Facturación", "Accesos", "Hardware", "Software", "Red"] as const;
const AGENTES = ["Carlos Herrera", "Lucía Morales", "Andrés Flores", "Valeria Ruiz", "Miguel Navarro"] as const;
const PRIORIDADES = ["Alta", "Media", "Media", "Baja"] as const;

const rnd = seededRandom(6620);
const START = excelDate("2026-02-02");
// Ticket, Apertura, Área, Prioridad, Agente, Estado, Horas resolución, Satisfacción
const ROWS = Array.from({ length: 56 }, (_, i) => {
  const agente = AGENTES[i % AGENTES.length];
  const roll = rnd.next();
  const estado = roll < 0.62 ? "Cerrado" : roll < 0.82 ? "Abierto" : "En espera";
  const cerrado = estado === "Cerrado";
  const horas: CellValue = cerrado ? rnd.int(1, 60) : "";
  const satisfaccion: CellValue = cerrado && rnd.next() < 0.7 ? rnd.int(1, 5) : "";
  return [
    `TK-${3100 + i}`,
    START + rnd.int(0, 75),
    rnd.pick(AREAS),
    rnd.pick(PRIORIDADES),
    agente,
    estado,
    horas,
    satisfaccion,
  ] as [string, number, string, string, string, string, CellValue, CellValue];
})
  .sort((a, b) => a[1] - b[1])
  .map((row, i) => [`TK-${3100 + i}`, ...row.slice(1)] as typeof row);

type Row = (typeof ROWS)[number];
const count = (filter: (r: Row) => boolean) => ROWS.filter(filter).length;

const IN = {
  prioridad: "Alta",
  desde: excelDate("2026-03-01"),
  hasta: excelDate("2026-03-31"),
};

const cerrados = (agente: string) => count((r) => r[4] === agente && r[5] === "Cerrado");

export const spec: FuncionProjectSpec = {
  projectId: "funciones-contarsiconjunto",
  sheets: [
    {
      name: SHEET,
      title: "Mesa de ayuda",
      subtitle: "Tickets de soporte de febrero a abril — responde en las celdas amarillas",
      tables: [
        {
          name: TABLE,
          anchor: "A4",
          headers: ["Ticket", "Apertura", "Área", "Prioridad", "Agente", "Estado", "Horas resolución", "Satisfacción"],
          rows: ROWS,
          formats: { Apertura: "dd/mm/yyyy" },
        },
        {
          name: RESUMEN,
          anchor: "J16",
          headers: ["Agente"],
          rows: AGENTES.map((agente) => [agente]),
          answerColumns: ["Cerrados", "Pendientes", "% SLA"],
          formats: { "% SLA": "0%" },
        },
      ],
      inputs: [
        { address: "K4", label: "Prioridad", value: IN.prioridad },
        { address: "K5", label: "Desde", value: IN.desde, format: "dd/mm/yyyy" },
        { address: "K6", label: "Hasta", value: IN.hasta, format: "dd/mm/yyyy" },
      ],
      answers: [
        { address: "K9", label: "Tarea 1 · Tickets con esa prioridad" },
        { address: "K10", label: "Tarea 2 · Urgentes sin cerrar" },
        { address: "K11", label: "Tarea 3 · Resueltos en más de 24 h" },
        { address: "K12", label: "Tarea 4 · Cerrados sin encuesta" },
        { address: "K13", label: "Tarea 5 · Abiertos en el periodo" },
      ],
    },
  ],
  checks: {
    1: { kind: "cell", sheet: SHEET, address: "K9", fns: ["COUNTIFS"], expected: count((r) => r[3] === IN.prioridad) },
    2: {
      kind: "cell", sheet: SHEET, address: "K10", fns: ["COUNTIFS"],
      expected: count((r) => r[3] === IN.prioridad && r[5] === "Abierto"),
    },
    3: {
      kind: "cell", sheet: SHEET, address: "K11", fns: ["COUNTIFS"],
      expected: count((r) => typeof r[6] === "number" && r[6] > 24),
    },
    4: {
      kind: "cell", sheet: SHEET, address: "K12", fns: ["COUNTIFS"],
      expected: count((r) => r[5] === "Cerrado" && r[7] === ""),
    },
    5: {
      kind: "cell", sheet: SHEET, address: "K13", fns: ["COUNTIFS"],
      expected: count((r) => r[1] >= IN.desde && r[1] <= IN.hasta),
    },
    6: {
      kind: "column", sheet: SHEET, table: RESUMEN, column: "Cerrados", fns: ["COUNTIFS"],
      expected: AGENTES.map(cerrados),
    },
    7: {
      kind: "column", sheet: SHEET, table: RESUMEN, column: "Pendientes", fns: ["COUNTIFS"],
      minCounts: { COUNTIFS: 2 },
      expected: AGENTES.map((a) => count((r) => r[4] === a && (r[5] === "Abierto" || r[5] === "En espera"))),
    },
    8: {
      kind: "column", sheet: SHEET, table: RESUMEN, column: "% SLA", fns: ["COUNTIFS"],
      expected: AGENTES.map(
        (a) =>
          count((r) => r[4] === a && r[5] === "Cerrado" && typeof r[6] === "number" && r[6] <= 24) /
          cerrados(a)
      ),
    },
  },
};

const F = (text: string) => `<span class="formula">${text}</span>`;

export const project: ProjectDefinition = {
  id: "funciones-contarsiconjunto",
  title: "CONTAR.SI.CONJUNTO: Mesa de ayuda",
  subtitle: "Contar filas que cumplen varias condiciones",
  description:
    "Coordinas la mesa de ayuda de una empresa. Cada semana presentas cuántos tickets llegaron, cuántos siguen abiertos, cuántos tardaron demasiado y cómo va cada agente contra el SLA de 24 horas.\n\nCONTAR.SI.CONJUNTO cuenta las filas que cumplen todas las condiciones. A diferencia de SUMAR.SI.CONJUNTO, no tiene rango a sumar: empieza directo con los pares rango–criterio.",
  topics: [
    "Contar con uno y varios criterios",
    "Operadores > y <= sobre números",
    "Contar celdas vacías con \"\"",
    "Rangos de fechas",
    "Resumen por agente y un indicador de SLA",
  ],
  tasks: [
    {
      id: 1,
      level: "basico",
      title: "Tarea 1: Tickets por prioridad",
      description:
        "En la hoja <strong>AG Tickets</strong>, celda <strong>K9</strong>: cuenta cuántos tickets tienen la <strong>Prioridad</strong> escrita en <strong>K4</strong>.",
      solution: [
        `Selecciona ${F("K9")}.`,
        `Escribe ${F("=CONTAR.SI.CONJUNTO(Tickets[Prioridad],K4)")}.`,
      ],
      expectedResult: "Número de tickets de prioridad Alta",
      finalFormula: "=CONTAR.SI.CONJUNTO(Tickets[Prioridad],K4)",
    },
    {
      id: 2,
      level: "basico",
      title: "Tarea 2: Urgentes sin cerrar",
      description: conditions("En <strong>K10</strong>: cuenta los tickets que cumplen <strong>las dos</strong> condiciones:", [
        "Prioridad igual a la de <strong>K4</strong>",
        "Estado <strong>Abierto</strong>",
      ]),
      solution: [
        `Selecciona ${F("K10")}.`,
        `Escribe ${F('=CONTAR.SI.CONJUNTO(Tickets[Prioridad],K4,Tickets[Estado],"Abierto")')}.`,
      ],
      expectedResult: "Tickets de prioridad Alta que siguen abiertos",
      finalFormula: '=CONTAR.SI.CONJUNTO(Tickets[Prioridad],K4,Tickets[Estado],"Abierto")',
    },
    {
      id: 3,
      level: "basico",
      title: "Tarea 3: Resueltos fuera de tiempo",
      description:
        "En <strong>K11</strong>: cuenta los tickets cuyas <strong>Horas resolución</strong> sean <strong>mayores a 24</strong>.",
      solution: [
        `Selecciona ${F("K11")}.`,
        "Los tickets abiertos tienen las horas vacías; el criterio \">24\" los ignora solo.",
        `Escribe ${F('=CONTAR.SI.CONJUNTO(Tickets[Horas resolución],">24")')}.`,
      ],
      expectedResult: "Tickets que tardaron más de un día",
      finalFormula: '=CONTAR.SI.CONJUNTO(Tickets[Horas resolución],">24")',
    },
    {
      id: 4,
      level: "intermedio",
      title: "Tarea 4: Cerrados sin encuesta",
      description: conditions(
        "En <strong>K12</strong>: cuenta los tickets que cumplen <strong>las dos</strong> condiciones:",
        ["Estado <strong>Cerrado</strong>", "<strong>Satisfacción vacía</strong> (el cliente no contestó la encuesta)"]
      ),
      solution: [
        `Selecciona ${F("K12")}.`,
        `Para contar celdas vacías, el criterio es ${F('""')} (comillas sin nada en medio).`,
        `Escribe ${F('=CONTAR.SI.CONJUNTO(Tickets[Estado],"Cerrado",Tickets[Satisfacción],"")')}.`,
      ],
      expectedResult: "Tickets cerrados sin calificación",
      finalFormula: '=CONTAR.SI.CONJUNTO(Tickets[Estado],"Cerrado",Tickets[Satisfacción],"")',
    },
    {
      id: 5,
      level: "intermedio",
      title: "Tarea 5: Tickets del periodo",
      description:
        "En <strong>K13</strong>: cuenta los tickets cuya <strong>Apertura</strong> esté entre la fecha de <strong>K5</strong> y la de <strong>K6</strong> (incluidas).",
      solution: [
        `Selecciona ${F("K13")}.`,
        `Dos condiciones sobre la misma columna: ${F('">="&K5')} y ${F('"<="&K6')}.`,
        `Escribe ${F('=CONTAR.SI.CONJUNTO(Tickets[Apertura],">="&K5,Tickets[Apertura],"<="&K6)')}.`,
      ],
      expectedResult: "Tickets abiertos en marzo",
      finalFormula: '=CONTAR.SI.CONJUNTO(Tickets[Apertura],">="&K5,Tickets[Apertura],"<="&K6)',
    },
    {
      id: 6,
      level: "intermedio",
      title: "Tarea 6: Cerrados por agente",
      description:
        "En la tabla <strong>ResumenAgentes</strong> (J16), columna <strong>Cerrados</strong>: cuenta los tickets <strong>Cerrados</strong> de cada agente.",
      solution: [
        `Selecciona ${F("K17")}.`,
        `${F("[@Agente]")} toma el nombre de la misma fila del resumen.`,
        `Escribe ${F('=CONTAR.SI.CONJUNTO(Tickets[Agente],[@Agente],Tickets[Estado],"Cerrado")')}.`,
      ],
      expectedResult: "Tickets cerrados de cada agente",
      finalFormula: '=CONTAR.SI.CONJUNTO(Tickets[Agente],[@Agente],Tickets[Estado],"Cerrado")',
    },
    {
      id: 7,
      level: "reto",
      title: "Tarea 7: Pendientes por agente",
      description: conditions(
        "En la columna <strong>Pendientes</strong>: cuenta los tickets de cada agente que estén en <strong>cualquiera</strong> de estos estados:",
        ["<strong>Abierto</strong>", "<strong>En espera</strong>"],
        "Como es una condición O, suma dos CONTAR.SI.CONJUNTO."
      ),
      solution: [
        `Selecciona ${F("L17")}.`,
        `Escribe ${F('=CONTAR.SI.CONJUNTO(Tickets[Agente],[@Agente],Tickets[Estado],"Abierto")+CONTAR.SI.CONJUNTO(Tickets[Agente],[@Agente],Tickets[Estado],"En espera")')}.`,
      ],
      expectedResult: "Tickets sin cerrar de cada agente",
      finalFormula:
        '=CONTAR.SI.CONJUNTO(Tickets[Agente],[@Agente],Tickets[Estado],"Abierto")+CONTAR.SI.CONJUNTO(Tickets[Agente],[@Agente],Tickets[Estado],"En espera")',
    },
    {
      id: 8,
      level: "reto",
      title: "Tarea 8: Cumplimiento de SLA",
      description: conditions(
        "En la columna <strong>% SLA</strong>: qué porcentaje de los tickets cerrados de cada agente se resolvió <strong>en 24 horas o menos</strong>.",
        [
          "Arriba: tickets del agente, <strong>Cerrados</strong> y con <strong>Horas resolución &lt;= 24</strong>",
          "Abajo: el total de <strong>Cerrados</strong> del agente (la columna de la Tarea 6)",
        ]
      ),
      solution: [
        `Selecciona ${F("M17")}.`,
        `Escribe ${F('=CONTAR.SI.CONJUNTO(Tickets[Agente],[@Agente],Tickets[Estado],"Cerrado",Tickets[Horas resolución],"<=24")/[@Cerrados]')}.`,
        "La columna ya tiene formato de porcentaje.",
      ],
      expectedResult: "Porcentaje dentro de SLA por agente",
      finalFormula:
        '=CONTAR.SI.CONJUNTO(Tickets[Agente],[@Agente],Tickets[Estado],"Cerrado",Tickets[Horas resolución],"<=24")/[@Cerrados]',
    },
  ],
};
